import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Button, Space, Popconfirm, Switch, Tag } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { packCategoryService } from "../../../services/settings-services";
import { packCategoryActions } from "../../../store/slices/settings-slices";
import { PackCategoryCreateModal } from "../settingSectionModals/PackCategoryCreateModal/PackCategoryCreateModal";
import { PackCategoryUpdateModal } from "../settingSectionModals/PackCategoryUpdateModal/PackCategoryUpdateModal";

const PackCategoriesComponent = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const { packCategories, status, trigger } = useSelector((store) => store.packCategories);
  const loading = status === "Loading";

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    dispatch(packCategoryActions.getAllPackCategories());
  }, [trigger, dispatch]);

  const handleDelete = async (id) => {
    try {
      await packCategoryService.deleteById(id);
      dispatch(packCategoryActions.changeTrigger());
      toast.success(t("Pack category deleted successfully"));
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error deleting pack category"));
    }
  };

  const handleToggleActive = async (id, currentValue) => {
    try {
      await packCategoryService.updateById(id, { isActive: !currentValue });
      dispatch(packCategoryActions.changeTrigger());
      toast.success(!currentValue ? t("Category activated") : t("Category deactivated"));
    } catch (error) {
      toast.error(t("Error updating category status"));
    }
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: t("Icon"),
      key: "icon",
      width: 70,
      render: (_, record) =>
        record.icon ? (
          <img src={record.icon} alt="icon" style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }} />
        ) : null,
    },
    {
      title: t("Category Name"),
      dataIndex: "name",
      key: "name",
      render: (name) => name?.[currentLanguage] || name?.en || "",
    },
    {
      title: t("Packs"),
      key: "packsCount",
      width: 100,
      render: (_, record) => record.packs?.length ?? 0,
    },
    {
      title: t("Status"),
      key: "isActive",
      width: 140,
      render: (_, record) => (
        <Space>
          <Switch
            checked={record.isActive}
            onChange={() => handleToggleActive(record.id, record.isActive)}
            size="small"
          />
          <Tag color={record.isActive ? "green" : "red"}>
            {record.isActive ? t("Active") : t("Inactive")}
          </Tag>
        </Space>
      ),
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => { setSelectedCategory(record); setIsUpdateModalOpen(true); }}
          >
            {t("Edit")}
          </Button>
          <Popconfirm
            title={t("Are you sure you want to delete this category?")}
            onConfirm={() => handleDelete(record.id)}
            okText={t("Yes")}
            cancelText={t("No")}
          >
            <Button type="primary" danger icon={<DeleteOutlined />} size="small">
              {t("Delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <section style={{ padding: "1rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h2 style={{ margin: 0 }}>{t("Pack Categories")}</h2>
        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setIsCreateModalOpen(true)}>
          {t("Create Pack Category")}
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={packCategories}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${t("Total")} ${total} ${t("items")}` }}
        bordered
      />

      {isCreateModalOpen && (
        <PackCategoryCreateModal isOpen={isCreateModalOpen} setIsOpen={setIsCreateModalOpen} />
      )}

      {isUpdateModalOpen && (
        <PackCategoryUpdateModal
          isOpen={isUpdateModalOpen}
          setIsOpen={setIsUpdateModalOpen}
          category={selectedCategory}
        />
      )}
    </section>
  );
};

export { PackCategoriesComponent };
